/* EntityShell — shared layout for every directory DETAIL page:
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │  header (title · meta)                                │
 *   ├──────────────────────────────────────┬───────────────┤
 *   │                                      │               │
 *   │  main content (charts, tables, …)    │  News ticker  │
 *   │                                      │  (right-rail) │
 *   │                                      │               │
 *   └──────────────────────────────────────┴───────────────┘
 *
 * The news ticker only renders when `entityType` + `entityId` are
 * provided (Clients / Services / Industries — not People).
 */
import React from 'react';
import { Icon } from './primitives.jsx';
import { NewsTicker } from './news-ticker.jsx';

export const EntityShell = ({
  // Required header fields
  title,
  // Optional header bits
  subtitle,
  badges,                  // array of { label, color? }
  meta,                    // right-aligned compact metadata, e.g. "12 projects · 4 people"
  avatar,                  // optional node rendered large to the LEFT of the title (People page)
  // News ticker — leave undefined for People (no public web news)
  entityType,              // 'client' | 'service' | 'industry' | undefined
  entityId,                // matching id
  // Optional node rendered at the right of the header (e.g. the
  // inline-edit View/Edit switch). Hidden on mobile via CSS — the
  // floating switch takes over there.
  headerAction,
  // Body
  children,
  rightRail,               // optional override for the right rail (defaults to NewsTicker)
}) => {
  const rail = rightRail !== undefined
    ? rightRail
    : (entityType && entityId ? <NewsTicker entityType={entityType} entityId={entityId}/> : null);

  return (
    <div className="entity-shell">
      <div className="entity-shell__head">
        {avatar && <div className="entity-shell__avatar" style={{flexShrink:0}}>{avatar}</div>}
        <div style={{flex:1,minWidth:0}}>
          <h1 className="entity-shell__title">{title}</h1>
          {subtitle && <div className="entity-shell__subtitle">{subtitle}</div>}
          {badges && badges.length > 0 && (
            <div className="entity-shell__badges">
              {badges.map((b, i) => {
                const style = b.color ? { background: b.color + '22', color: b.color } : undefined;
                return b.href ? (
                  <a key={i} href={b.href} title={b.title || `Open ${b.label}`} className="entity-shell__badge" style={{ textDecoration: 'none', ...style }}>
                    {b.label}
                  </a>
                ) : (
                  <span key={i} className="entity-shell__badge" style={style}>
                    {b.label}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        {meta && <div className="entity-shell__meta">{meta}</div>}
        {headerAction && <div className="entity-shell__action">{headerAction}</div>}
      </div>

      <div className={`entity-shell__body ${rail ? 'entity-shell__body--with-rail' : ''}`}>
        <div className="entity-shell__main">
          {children}
        </div>
        {rail && (
          <div className="entity-shell__rail">
            {rail}
          </div>
        )}
      </div>
    </div>
  );
};

/* A tiny helper for sections inside the main column. Keeps padding
 * + heading consistent so every detail view doesn't reinvent it. */
export const EntitySection = ({ title, action, children }) => (
  <section className="entity-shell__section">
    {(title || action) && (
      <div className="entity-shell__section-head">
        {title && <h2>{title}</h2>}
        <div style={{flex:1}}/>
        {action}
      </div>
    )}
    <div className="entity-shell__section-body">{children}</div>
  </section>
);

void Icon;  // keep import live for future header glyphs
